import { motion } from "framer-motion";
import { ArrowDown, GithubLogo, EnvelopeSimple } from "@phosphor-icons/react";

const metrics = [
  { value: "40%", label: "Faster API Performance", desc: "Backend optimization" },
  { value: "60%", label: "Lower Analytics Latency", desc: "Elasticsearch migration" },
  { value: "3x", label: "Meeting Capture Adoption", desc: "Product-led growth" },
  { value: "70%", label: "Faster Deployments", desc: "CI/CD modernization" },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export const Hero = () => {
  const scrollToWork = () => {
    const el = document.querySelector("#work");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center grid-background overflow-hidden"
    >
      {/* Subtle top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-radial from-[rgba(56,189,248,0.04)] to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-20">
        <div className="max-w-4xl">
          {/* Tag */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8"
          >
            <span className="inline-flex items-center gap-2 text-[13px] font-mono text-[#A1A1AA] bg-[#121212] border border-white/[0.08] rounded-full px-4 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Currently building at
              <a href="https://zime.ai" target="_blank" rel="noopener noreferrer" className="text-[#EDEDED] hover:text-white transition-colors underline decoration-white/20 underline-offset-2">Zime.ai</a>
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-headline"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#EDEDED] leading-[1.08] mb-6"
          >
            Building scalable product systems, AI-powered workflows, and operational intelligence platforms.
          </motion.h1>

          {/* Subline */}
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-subline"
            className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl leading-relaxed mb-10"
          >
            Lead Full Stack Engineer focused on scalable architecture, product workflows, and modern web systems.
          </motion.p>

          {/* CTAs */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-3 mb-20"
          >
            <button
              onClick={scrollToWork}
              data-testid="hero-cta-work"
              className="bg-white text-black hover:bg-gray-200 transition-colors font-medium rounded-full px-6 py-2.5 text-sm flex items-center gap-2"
            >
              View Selected Work
              <ArrowDown size={14} weight="bold" />
            </button>
            <a
              href="https://drive.google.com/file/d/1smdR0_KYP7OnT3UZl3fnY9vD-pJShwcJ/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-resume"
              className="bg-transparent border border-white/[0.1] text-[#EDEDED] hover:bg-white/[0.05] transition-colors rounded-full px-6 py-2.5 text-sm font-medium"
            >
              Resume
            </a>
            <a
              href="https://github.com/goyal079"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-github"
              className="bg-transparent border border-white/[0.1] text-[#EDEDED] hover:bg-white/[0.05] transition-colors rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2"
            >
              <GithubLogo size={16} />
              GitHub
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-cta-contact"
              className="bg-transparent border border-white/[0.1] text-[#EDEDED] hover:bg-white/[0.05] transition-colors rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2"
            >
              <EnvelopeSimple size={16} />
              Contact
            </a>
          </motion.div>
        </div>

        {/* Metrics */}
        <motion.div
          initial="initial"
          animate="animate"
          transition={{ staggerChildren: 0.1, delayChildren: 0.6 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="hero-metrics"
        >
          {metrics.map((m, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="metric-glow bg-[#121212] border border-white/[0.08] rounded-xl p-5 hover:border-white/[0.15] transition-all duration-300"
              data-testid={`hero-metric-${i}`}
            >
              <div className="font-heading text-2xl sm:text-3xl font-bold text-[#EDEDED] mb-1">
                {m.value}
              </div>
              <div className="text-sm font-medium text-[#EDEDED] mb-0.5">{m.label}</div>
              <div className="text-xs text-[#A1A1AA] font-mono">{m.desc}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
